import React, { useState, useEffect, useRef } from 'react';
import { basisFromCase } from '../utils/dataBasis';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import API from '../services/api';
import { ChevronLeftIcon, ExclamationTriangleIcon, DocumentIcon, TrashIcon } from '@heroicons/react/24/outline';
import CaseFormWizard from '../components/CaseFormWizard';
import { CaseFormData, initialFormData, developmentProjectsFromCase, developmentProjectsToApi } from '../types/caseForm';
import { uploadCaseDocuments, describeUploadFailures } from '../services/documentsApi';
import { toPlainDate, plainDateToIso } from '../utils/plainDate';
import { serializeForm, type AutosaveState } from '../utils/formSerialize';
import { formatCaseDate } from '../utils/formatDateValue';

// The PUT body for a form state. Shared by the explicit save and the draft autosave so the two
// can never disagree about what a field maps to.
const buildUpdatePayload = (formData: CaseFormData): Record<string, any> => {
  // Build the start date based on precision
  let processedStartDate = formData.caseStartDate;
  if (formData.caseStartDatePrecision === 'YEAR' && formData.caseStartDate) {
    processedStartDate = `${formData.caseStartDate}-01-01`;
  } else if (formData.caseStartDatePrecision === 'MONTH' && formData.caseStartDate) {
    processedStartDate = `${formData.caseStartDate}-01`;
  }

  const incidentDateISO = processedStartDate ? plainDateToIso(processedStartDate) : null;

  const updateData: Record<string, any> = {
    incidentDate: incidentDateISO,
    description: formData.description,
    violationTypes: formData.violationTypes,
    country: formData.country,
    indigenousGroup: formData.indigenousGroup.join(', '),
    documentorName: formData.documentorName,
    documentorOrganization: formData.documentorOrganization,
    documentorEmail: formData.documentorEmail,
    documentorPhone: formData.documentorPhone,
    isPrimaryData: formData.isPrimaryData,
    dataBasis: formData.dataBasis,
    dataSource: 'Web',
    dataUsageRestriction: formData.dataUsageRestriction,
    willingToShareMoreInfo: formData.willingToShareMoreInfo,
    isLDSFApplicant: formData.isLDSFApplicant,
    ldsfPaymentCompletedDate: formData.isLDSFApplicant && formData.ldsfPaymentCompletedDate ? formData.ldsfPaymentCompletedDate : null,
    sourceInformation: formData.sourceInformation,
    incidentTime: formData.incidentTime,
    otherViolationTypes: formData.otherViolationTypes,
    otherViolationTypeDetails: formData.otherViolationTypeDetails,
    isCaseOngoing: formData.isCaseOngoing,
    caseStartDate: processedStartDate,
    caseStartDatePrecision: formData.caseStartDatePrecision,
    caseEndDate: formData.caseEndDate || null,
    // Never send null here: the backend would parse it as 1970. Omit to leave it unchanged.
    ...(formData.reportedDate ? { reportedDate: formData.reportedDate } : {}),
    region: formData.region,
    city: formData.city,
    specificLocation: formData.specificLocation,
    latitude: formData.latitude ? parseFloat(formData.latitude) : null,
    longitude: formData.longitude ? parseFloat(formData.longitude) : null,
    indigenousTerritory: formData.indigenousTerritory,
    violationDetails: formData.violationDetails,
    familyImpact: formData.familyImpact,
    communityImpact: formData.communityImpact,
    impactTypes: formData.impactTypes,
    investigationConducted: formData.investigationConducted,
    investigationDetails: formData.investigationDetails,
    investigationWhyNot: formData.investigationWhyNot,
    advocacyUndertaken: formData.advocacyUndertaken,
    advocacyDetails: formData.advocacyDetails,
    advocacyWhyNot: formData.advocacyWhyNot,
    isRelatedToDevelopmentProject: formData.isRelatedToDevelopmentProject,
    developmentProjects: developmentProjectsToApi(formData.developmentProjects),
    victims: formData.victims,
    perpetrators: formData.perpetrators.filter(p => String(p.name || '').trim()),
    priority: formData.priority,
    tags: formData.tags,
    isPublic: formData.isPublic,
  };

  return updateData;
};

const EditCase: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialStep = Number(searchParams.get('step')) || undefined;
  const navigate = useNavigate();
  const { user } = useAuth();
  // ADMINs may save a case that fails validation, but only after confirming the wizard's dialog —
  // which is what sets `bypassValidation` on the requests below.
  const isAdmin = user?.role === 'ADMIN';
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // loadError: the case could not be fetched (full-page state). error: a save failed (banner
  // above the form, edits kept).
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [formData, setFormData] = useState<CaseFormData>(initialFormData);
  const [caseStatus, setCaseStatus] = useState<string>('');
  const [isAutoSourced, setIsAutoSourced] = useState(false);
  // AI provenance for the field marks; refreshed on its own after Confirm/Undo so edits survive.
  const [aiMeta, setAiMeta] = useState<{ autoApplied?: any[] } | null>(null);
  const refreshAiMeta = async () => {
    try { const r = await API.get(`/api/cases/${id}`); setAiMeta(r.data.case?.metadata?.ai ?? null); } catch { /* keep the last known marks */ }
  };

  // Draft autosave: while a DRAFT is being edited, PUT the form a few seconds after the last
  // change. Files are not part of it (they upload on Save). Nothing runs until the form differs
  // from what was loaded or last saved.
  const [savedJson, setSavedJson] = useState<string | null>(null);
  const [autosave, setAutosave] = useState<AutosaveState>({ status: 'idle', savedAt: null, where: 'server' });
  const autosaveTimer = useRef<number | null>(null);
  const autosaveNow = async (fd: CaseFormData) => {
    const json = serializeForm(fd);
    setAutosave(a => ({ ...a, status: 'saving' }));
    try {
      await API.put(`/api/cases/${id}`, { ...buildUpdatePayload(fd), autosave: true });
      setSavedJson(json);
      setAutosave({ status: 'saved', savedAt: Date.now(), where: 'server' });
    } catch {
      setAutosave({ status: 'error', savedAt: null, where: 'server', onRetry: () => { void autosaveNow(fd); } });
    }
  };
  useEffect(() => {
    if (loading || saving || caseStatus !== 'DRAFT' || savedJson === null) return;
    if (serializeForm(formData) === savedJson) return;
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => { void autosaveNow(formData); }, 4000);
    return () => { if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, loading, saving, caseStatus, savedJson]);

  useEffect(() => {
    if (id) {
      fetchCase();
      fetchDocuments();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCase = async () => {
    try {
      const response = await API.get(`/api/cases/${id}`);
      const caseData = response.data.case;
      setCaseStatus(caseData.status || '');
      setIsAutoSourced(!!caseData.isAutoSourced);
      setAiMeta(caseData.metadata?.ai ?? null);

      const loaded: CaseFormData = {
        ...initialFormData,
        // Documenter Information
        documentorName: caseData.documentorName || '',
        documentorOrganization: caseData.documentorOrganization || '',
        documentorEmail: caseData.documentorEmail || '',
        documentorPhone: caseData.documentorPhone || '',
        isPrimaryData: caseData.isPrimaryData || false,
        dataBasis: basisFromCase(caseData) || 'PRIMARY',
        dataSource: caseData.dataSource || '',
        dataUsageRestriction: caseData.dataUsageRestriction || '',
        willingToShareMoreInfo: caseData.willingToShareMoreInfo || false,
        isLDSFApplicant: caseData.isLDSFApplicant || false,
        ldsfPaymentCompletedDate: toPlainDate(caseData.ldsfPaymentCompletedDate),
        sourceInformation: caseData.sourceInformation || '',

        // Incident Details
        incidentTime: caseData.incidentTime || '',
        description: caseData.description || '',
        violationTypes: caseData.violationTypes || [],
        otherViolationTypes: caseData.otherViolationTypes || [],
        otherViolationTypeDetails: caseData.otherViolationTypeDetails || '',
        isCaseOngoing: caseData.isCaseOngoing || false,
        caseStartDate: (() => {
          const raw = caseData.caseStartDate || caseData.incidentDate;
          if (!raw) return '';
          const fullDate = toPlainDate(raw); // YYYY-MM-DD, UTC calendar day as stored
          const precision = caseData.caseStartDatePrecision || 'DAY';
          if (precision === 'YEAR') return fullDate.slice(0, 4); // "2025"
          if (precision === 'MONTH') return fullDate.slice(0, 7); // "2025-06"
          return fullDate; // "2025-06-16"
        })(),
        caseStartDatePrecision: caseData.caseStartDatePrecision || 'DAY',
        caseEndDate: toPlainDate(caseData.caseEndDate),
        reportedDate: toPlainDate(caseData.reportedDate),

        // Location
        country: caseData.country || '',
        region: caseData.region || '',
        city: caseData.city || '',
        specificLocation: caseData.specificLocation || '',
        latitude: caseData.latitude ? String(caseData.latitude) : '',
        longitude: caseData.longitude ? String(caseData.longitude) : '',

        // Indigenous Group
        indigenousGroup: caseData.indigenousGroup
          ? caseData.indigenousGroup.split(',').map((s: string) => s.trim()).filter(Boolean)
          : [],
        indigenousTerritory: caseData.indigenousTerritory || '',

        // Violation Details (coerce null scalars to the form's ''/[] shape)
        violationDetails: (caseData.violationDetails || []).map((vd: any) => ({
          ...vd,
          criminalizationTypes: vd.criminalizationTypes || [],
          criminalizationTriggers: vd.criminalizationTriggers || [],
          legalInstrumentTypes: vd.legalInstrumentTypes || [],
          proceedingType: vd.proceedingType || '',
          perpetratorAccountabilityStatus: vd.perpetratorAccountabilityStatus || '',
        })),

        // Impact
        familyImpact: caseData.familyImpact || '',
        communityImpact: caseData.communityImpact || '',
        impactTypes: caseData.impactTypes || [],
        investigationConducted: caseData.investigationConducted ?? null,
        investigationDetails: caseData.investigationDetails || '',
        investigationWhyNot: caseData.investigationWhyNot || '',
        advocacyUndertaken: caseData.advocacyUndertaken ?? null,
        advocacyDetails: caseData.advocacyDetails || '',
        advocacyWhyNot: caseData.advocacyWhyNot || '',

        // Development Project
        isRelatedToDevelopmentProject: caseData.isRelatedToDevelopmentProject ?? null,
        developmentProjects: developmentProjectsFromCase(caseData),

        // Metadata
        priority: caseData.priority || 1,
        tags: caseData.tags || [],
        isPublic: caseData.isPublic || false,

        // Relations
        // Convert stored violation tags ({ violationType, otherViolationType }) back to
        // the flat keys the form grid uses (KILLING / CRIMINALIZATION / <other-type>).
        victims: (caseData.victims && caseData.victims.length
          ? caseData.victims.map((v: any) => {
              const details = Array.isArray(v.violationDetails) ? v.violationDetails : [];
              const keyOf = (t: any) => (t.violationType === 'OTHER' ? (t.otherViolationType || 'OTHER') : t.violationType);
              const DEMO = ['affectedCount','affectedMaleCount','affectedFemaleCount','affected0to12Count','affected13to25Count','affected26to35Count','affected36to59Count','affected60PlusCount'];
              // Per-person outcome fields on CRIMINALIZATION rows (non-numeric).
              const OUTCOME = ['accusationOutcome','detentionType','detentionDays'];
              const violationDemographics: { [k: string]: { [f: string]: number | string | string[] } } = {};
              details.forEach((t: any) => {
                const entry: { [f: string]: number | string | string[] } = {};
                DEMO.forEach((f) => { if (t[f] != null) entry[f] = t[f]; });
                OUTCOME.forEach((f) => { if (t[f] != null) entry[f] = t[f]; });
                if (Array.isArray(t.dueProcessViolations) && t.dueProcessViolations.length > 0) entry.dueProcessViolations = t.dueProcessViolations;
                violationDemographics[keyOf(t)] = entry;
              });
              return {
                ...v,
                groupType: v.groupType || '',
                communityRoleType: v.communityRoleType || '',
                violationTags: details.map(keyOf),
                violationDemographics,
              };
            })
          : [{ isGroup: false }]),
        perpetrators: (caseData.perpetrators || []).map((p: any) => ({ ...p, type: p.type || '', notes: p.notes || '' })),
        files: []
      };
      setFormData(loaded);
      // Baseline for autosave: nothing to save until the form differs from what was loaded.
      setSavedJson(serializeForm(loaded));

    } catch (err: any) {
      setLoadError(err.response?.data?.message || 'Failed to fetch case');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!id) return;
    setDocumentsLoading(true);
    try {
      const response = await API.get(`/api/documents/case/${id}`);
      setDocuments(response.data.documents || []);
    } catch (err: any) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const deleteDocument = async (documentId: string) => {
    const confirmed = await confirm({
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document?',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;
    try {
      await API.delete(`/api/documents/${documentId}`);
      await fetchDocuments();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to delete document');
    }
  };

  const downloadDocument = async (documentId: string) => {
    try {
      const response = await API.get(`/api/documents/${documentId}/download`);
      window.open(response.data.downloadUrl, '_blank');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to get download URL');
    }
  };

  const handleSubmit = async (asDraft: boolean, bypassValidation = false) => {
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    setSaving(true);
    setError('');

    try {
      const updateData = buildUpdatePayload(formData);

      // Only ever true when an admin confirmed the bypass dialog; the backend ignores it for
      // everyone else.
      if (bypassValidation) {
        updateData.bypassValidation = true;
      }

      await API.put(`/api/cases/${id}`, updateData);

      // Upload new files with their full metadata; report any that fail rather than hiding it.
      if (formData.files.length > 0) {
        const outcome = await uploadCaseDocuments(id!, formData.files);
        const failure = describeUploadFailures(outcome);
        if (failure) showToast('error', failure);
      }

      // If editing a draft and user chose Submit, transition the case to FOR_VALIDATION
      if (!asDraft && caseStatus === 'DRAFT') {
        await API.post(`/api/cases/${id}/submit`, bypassValidation ? { bypassValidation: true } : {});
      }

      navigate(`/cases/${id}`);
    } catch (err: any) {
      const data = err.response?.data;
      let msg = data?.message || 'Failed to update case';
      if (data?.missingFields?.length) {
        msg += ` Missing: ${data.missingFields.join(', ')}`;
      }
      setError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  // Existing documents panel to pass into wizard
  const existingDocumentsNode = (
    <>
      {documentsLoading ? (
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading documents...</div>
      ) : documents.length > 0 ? (
        <div className="space-y-2 mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Existing Documents</h3>
          {documents.map((doc: any) => (
            <div key={doc.id} className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DocumentIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <button
                      type="button"
                      onClick={() => downloadDocument(doc.id)}
                      className="text-sm font-medium text-emerald-600 hover:text-emerald-800"
                    >
                      {doc.fileName}
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(doc.fileSize / 1024).toFixed(1)} KB • {doc.fileType}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteDocument(doc.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
              {(doc.category || doc.description || doc.documentDate) && (
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 ml-7">
                  {doc.category && <span>Category: <span className="text-gray-700 dark:text-gray-300 font-medium">{doc.category}</span></span>}
                  {doc.documentDate && <span>Date: <span className="text-gray-700 dark:text-gray-300 font-medium">{formatCaseDate(doc.documentDate)}</span></span>}
                  {doc.confidentiality && doc.confidentiality !== 'INTERNAL' && (
                    <span className={`px-1.5 py-0.5 rounded font-medium ${doc.confidentiality === 'RESTRICTED' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                      {doc.confidentiality}
                    </span>
                  )}
                </div>
              )}
              {doc.description && (
                <p className="text-xs text-gray-600 dark:text-gray-400 ml-7">{doc.description}</p>
              )}
              {doc.sensitivityWarnings && doc.sensitivityWarnings.length > 0 && (
                <div className="flex flex-wrap gap-1 ml-7">
                  {doc.sensitivityWarnings.map((w: string) => (
                    <span key={w} className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">{w}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

    </>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mr-3"></div>
          Loading case...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Error loading case</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{loadError}</p>
        <div className="mt-6">
          <Link
            to="/cases"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-2" />
            Back to Cases
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link to={`/cases/${id}`} className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100">
            <ChevronLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Edit Case</h1>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      <CaseFormWizard
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        loading={saving}
        isEditing
        caseStatus={caseStatus}
        initialStep={initialStep}
        allowValidationBypass={isAdmin}
        cancelTo={`/cases/${id}`}
        isAutoSourced={isAutoSourced}
        autosave={caseStatus === 'DRAFT' ? autosave : undefined}
        cleanSnapshot={savedJson}
        aiMeta={aiMeta}
        caseId={id}
        onAiChange={refreshAiMeta}
        existingDocuments={existingDocumentsNode}
      />
    </div>
  );
};

export default EditCase;
